/**
 * Off-product art backdrop -- which surface to draw decoration art on.
 *
 * Decoration artwork shown OFF a garment (a library tile, a store
 * Decorations row, a picker) used to sit on whatever the page was, which
 * is usually white -- so white or pale art was invisible. This module
 * decides the backdrop; ``components/decoration/DecorationArtThumb.vue``
 * is the one component that applies it. Composited-on-garment renders
 * (the product editor Stage, Artifi previews) are NOT this concern: there
 * the garment is the backdrop.
 *
 * INPUT, best first:
 *   1. ``Artwork.dominant_ink_hex`` -- sampled server-side from the actual
 *      pixels (api/src/services/artwork_luminance.py, ADR 0062) and
 *      serialized on the payloads that feed the off-product surfaces.
 *   2. ``sampleImageInk`` below -- the same average-of-opaque-pixels, read
 *      from the VISIBLE <img> once it has loaded, for a surface whose
 *      payload has no server value (the colorway editor's recolor preview,
 *      a data: URL). ONLY for pixels readable without CORS: data:, blob:,
 *      same-origin. Production artwork is served from a CloudFront
 *      distribution whose cache key ignores Origin, so a CORS read of an
 *      image the page already loaded no-cors gets a cached response with no
 *      Access-Control-Allow-Origin -- it fails, and logs a console error.
 *      Cross-origin images are therefore never sampled (#4075 gate P1-1).
 *   3. Unknown -> the caller's unknown-ink backdrop (neutral by default, on
 *      which both white and black ink stay visible).
 *
 * The decision is WCAG contrast, not a luminance threshold: the art goes
 * on whichever of the light/dark backdrop gives its ink more contrast.
 * The math is ``contrastRatio`` in ``@/utils/luminance`` -- not
 * reimplemented here.
 */
import { contrastRatio, luminanceFromRgb } from '@/utils/luminance';

/** Mirrors the ``--tf-art-backdrop-*`` tokens in styles/_tokens.css. */
export const ART_BACKDROP_HEX = Object.freeze({
    light: '#ffffff',
    dark: '#37474f',
    neutral: '#8c959d',
});

const CHECKER_CSS =
    'repeating-conic-gradient(#eceff1 0% 25%, #ffffff 0% 50%) 0 0 / 16px 16px';

/**
 * CSS ``background`` value per backdrop key. ``checker`` is a manual
 * choice only (the detail page's backdrop switcher); auto never picks it.
 */
export const ART_BACKDROP_CSS = Object.freeze({
    light: `var(--tf-art-backdrop-light, ${ART_BACKDROP_HEX.light})`,
    dark: `var(--tf-art-backdrop-dark, ${ART_BACKDROP_HEX.dark})`,
    neutral: `var(--tf-art-backdrop-neutral, ${ART_BACKDROP_HEX.neutral})`,
    checker: CHECKER_CSS,
});

/**
 * Pick the backdrop for an ink color.
 *
 * @param {string|null|undefined} inkHex
 * @returns {'light'|'dark'|null} null when the ink is unknown/unparseable.
 */
export function pickArtBackdrop(inkHex) {
    if (typeof inkHex !== 'string' || inkHex.trim() === '') return null;
    const onLight = contrastRatio(inkHex, ART_BACKDROP_HEX.light);
    const onDark = contrastRatio(inkHex, ART_BACKDROP_HEX.dark);
    if (onLight === null || onDark === null) return null;
    return onDark > onLight ? 'dark' : 'light';
}

/**
 * The backdrop the SERVER-sampled ink decides, or null when it cannot.
 *
 * ``Artwork.dominant_ink_hex`` picks light or dark; two-tone art
 * (``dominant_ink_mixed``, #4079) suits neither, so it goes on neutral.
 * DecorationArtThumb applies this first, before any client sample; the
 * split light/dark dialog derives its suggestion from the same answer.
 *
 * @param {string|null|undefined} inkHex
 * @param {boolean} [inkMixed]
 * @returns {'light'|'dark'|'neutral'|null}
 */
export function resolveServerArtBackdrop(inkHex, inkMixed = false) {
    const pick = pickArtBackdrop(inkHex);
    if (!pick) return null;
    return inkMixed ? 'neutral' : pick;
}

function toHex2(n) {
    return Math.round(n).toString(16).padStart(2, '0');
}

/**
 * Average the RGB of every non-transparent pixel in an RGBA array.
 *
 * Same rule as the server sampler (alpha 0 is skipped, every other pixel
 * counts equally), so a client sample and a server sample of the same
 * image agree.
 *
 * @param {Uint8ClampedArray|number[]|null} data  Flat RGBA bytes.
 * @returns {string|null} ``#rrggbb``, or null when nothing is opaque.
 */
export function averageOpaqueHex(data) {
    const ink = inkFromPixels(data);
    return ink ? ink.hex : null;
}

/**
 * Mixed (two-tone) ink thresholds -- MIRRORS api/src/services/
 * artwork_luminance.py MIXED_INK_* (#4079; the rationale for each number is
 * documented there). Art is "mixed" when at least MIXED_INK_MIN_SHARE of its
 * opaque pixels are near-black AND at least that share are near-white; its
 * average is then a grey that suits neither half, so it gets the neutral
 * backdrop.
 */
export const MIXED_INK_DARK_MAX_L = 0.1;
export const MIXED_INK_LIGHT_MIN_L = 0.6;
export const MIXED_INK_MIN_SHARE = 0.2;

/**
 * The average opaque ink of an RGBA array plus the mixed-ink flag.
 *
 * @param {Uint8ClampedArray|number[]|null} data  Flat RGBA bytes.
 * @returns {{hex: string, mixed: boolean}|null} null when nothing is opaque.
 */
export function inkFromPixels(data) {
    if (!data || data.length < 4) return null;
    let r = 0;
    let g = 0;
    let b = 0;
    let count = 0;
    let dark = 0;
    let light = 0;
    for (let i = 0; i + 3 < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count += 1;
        const l = luminanceFromRgb({ r: data[i], g: data[i + 1], b: data[i + 2] });
        if (l <= MIXED_INK_DARK_MAX_L) dark += 1;
        else if (l >= MIXED_INK_LIGHT_MIN_L) light += 1;
    }
    if (count === 0) return null;
    return {
        hex: `#${toHex2(r / count)}${toHex2(g / count)}${toHex2(b / count)}`,
        mixed:
            dark / count >= MIXED_INK_MIN_SHARE && light / count >= MIXED_INK_MIN_SHARE,
    };
}

// Downsample before reading pixels -- the average of a 64px thumbnail is
// a fine proxy for the dominant ink and keeps the read cheap.
const SAMPLE_MAX_DIM = 64;

function originOf(src) {
    if (typeof window === 'undefined' || !window.location) return null;
    try {
        return new URL(src, window.location.href);
    } catch {
        return null;
    }
}

/**
 * Can this image's pixels be read without CORS?
 *
 * data: and blob: URLs never taint a canvas; a same-origin URL does not
 * either. Anything else -- the S3 / CloudFront / MinIO artwork hosts -- is
 * refused, so the browser never issues a CORS request for it.
 *
 * @param {string|null} src
 * @returns {boolean}
 */
export function canReadPixels(src) {
    if (typeof src !== 'string' || src === '') return false;
    if (src.startsWith('data:') || src.startsWith('blob:')) return true;
    const url = originOf(src);
    return Boolean(url) && url.origin === window.location.origin;
}

/** Default pixel reader: draw the loaded <img> small and read it back. */
function readPixelsFromElement(img) {
    if (typeof document === 'undefined') return null;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    const scale = Math.min(1, SAMPLE_MAX_DIM / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, cw, ch);
    return ctx.getImageData(0, 0, cw, ch).data;
}

/** Most samples kept; the least recently used is evicted beyond this. */
export const INK_SAMPLE_CACHE_MAX = 200;

// One sample per same-origin image path (query string dropped, so rotating
// signed URLs for one object share an entry). data:/blob: previews are not
// cached -- each is a fresh render and the key would pin the whole payload.
const sampleCache = new Map();

/** Test seam: forget every cached sample. */
export function resetInkSampleCache() {
    sampleCache.clear();
}

function cacheKey(src) {
    if (src.startsWith('data:') || src.startsWith('blob:')) return null;
    const url = originOf(src);
    return url ? url.origin + url.pathname : null;
}

/**
 * Sample the average opaque ink of an already-loaded <img>.
 *
 * Reads the visible element (so a lazy image is never fetched just to be
 * sampled, and no second request is made). Never throws: an unreadable
 * source, an unloaded image or a tainted canvas resolve to null.
 *
 * @param {HTMLImageElement|null} img
 * @param {{readPixels?: (img: HTMLImageElement) => ArrayLike<number>|null}} [opts]
 * @returns {Promise<{hex: string, mixed: boolean}|null>}
 */
export function sampleImageInk(img, { readPixels = readPixelsFromElement } = {}) {
    const src = img && (img.currentSrc || img.src);
    if (!src || !canReadPixels(src)) return Promise.resolve(null);
    if (!img.naturalWidth || !img.naturalHeight) return Promise.resolve(null);
    const key = cacheKey(src);
    if (key && sampleCache.has(key)) {
        const hit = sampleCache.get(key);
        sampleCache.delete(key);
        sampleCache.set(key, hit);
        return Promise.resolve(hit);
    }
    let ink = null;
    try {
        ink = inkFromPixels(readPixels(img));
    } catch {
        ink = null;
    }
    if (key) {
        sampleCache.set(key, ink);
        if (sampleCache.size > INK_SAMPLE_CACHE_MAX) {
            sampleCache.delete(sampleCache.keys().next().value);
        }
    }
    return Promise.resolve(ink);
}
